import { takeEvery, takeLatest, call, fork, put, select, all } from 'redux-saga/effects';
import { AccessLevel, AccountCore, BillingType, ProductType } from '@dev/ee-api/ee-api-models';
import isEmail from 'validator/lib/isEmail';
import { find, isEmpty, includes } from 'lodash-es';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { replace } from 'redux-first-history';
import {
    Sender,
    TransactionalRecipient,
    EmailTransactionalMessage,
    ContactStatus,
    Template,
    TemplateType,
} from '@dev/ee-api/ee-api-models';
import i18n from '@/../i18n';
import { TemplatesPaths, pathsParams } from '@/services/dictionaries/Paths';
import { getGeneralInfo } from '@/store/account/actions';
import {
    ERROR_TOAST_DURATION,
    INFO_TOAST_DURATION,
    // SUCCESS_TOAST_DURATION,
    INDEX_PLACEHOLDER,
} from '@/services/constants';
import { getDialogStore, HideDialog, UpdateDialog } from '@/store/dialog/actions';
import { getMediaManagerStore, HideMediaManager } from '@/store/mediaManager/actions';
import { loadSingleContact } from '@/services/ElasticApi';
import { FileLoadSuccess, getUserFilesStore } from '@/store/userfiles/actions';
import {
    getEditorFilesStore,
    LoadError,
    LoadSuccess,
    SaveError,
    SaveSuccess,
    SetDefaultTemplate,
    SetSendTestData,
    UploadEditorFileError,
    UploadEditorFileStart,
    UploadEditorFileSuccess,
} from '@/store/templateEditor/actions';
import {
    SendTestErrorNames,
    SendTestErrors,
    TemplateEditorActionTypes,
    emailLimitBackendErrors,
    BackendErrors,
    getTemplateEditor,
    UploadEditorFileAction,
    EditorFile,
    SaveStartAction,
    LoadStartAction,
} from '@/store/templateEditor/types';
import { Logger } from '@/services/errors/appLogger';
import { uploadFile } from '../services/ApiCategories/campaigns';
import { sendEmail } from '../services/ApiCategories/emails';
import { ShowToast, HideToast } from '../store/toast/actions';
import {
    getDefaultTemplateObj,
    loadTemplate as fetchTemplate,
    saveTemplate,
    updateTemplate,
} from '../services/ApiCategories/templates';
import { INVALID_FILENAME_CHARACTERS_REGEX } from './campaignCreator';

// const ShowSuccessToast = (message: string): any =>
//     ShowToast({
//         severity: 'success',
//         message: `${i18n.t('success')}: ${message}`,
//         duration: SUCCESS_TOAST_DURATION,
//         onClose: HideToast,
//     });
const ShowErrorToast = (e): any =>
    ShowToast({
        severity: 'error',
        message: `${i18n.t('error')}: ${e.response?.data?.Error || e.message}`,
        duration: ERROR_TOAST_DURATION,
        onClose: HideToast,
    });
const ShowInfoToast = (message: string): any =>
    ShowToast({
        severity: 'info',
        message,
        duration: INFO_TOAST_DURATION,
        onClose: HideToast,
    });

function* getContactStatus(email: string) {
    try {
        const contact = yield call(loadSingleContact, email);
        return contact.Status;
    } catch (e) {
        return 'Error';
    }
}

function* verifySendTest(recipients?: string[], backendError?: string) {
    const errors: SendTestErrors = {};
    const { EmailSizeLimit } = yield select(getGeneralInfo);
    const {
        AccountAccessLevel,
        ProductType: accProductType,
        BillingType: accBillingType,
    }: AccountCore = yield select(getGeneralInfo);

    const isApiNewOrOld = accProductType === ProductType.API || accBillingType === BillingType.PayAsYouGo;

    if (!isEmpty(recipients)) {
        const emailsInvalid = !!find(recipients, (email) => !isEmail(email));
        if (emailsInvalid) {
            errors[SendTestErrorNames.INVALID_EMAILS] = true;
        } else if (AccountAccessLevel?.includes(AccessLevel.ViewContacts) && !isApiNewOrOld) {
            const recipientsStatuses = yield all(recipients.map((email) => call(getContactStatus, email)));
            const emailsStatusInvalid = !!find(
                recipientsStatuses,
                (status) => status !== ContactStatus.Active && status !== ContactStatus.Engaged
            );
            errors[SendTestErrorNames.INVALID_EMAILS_STATUS] = emailsStatusInvalid;
        }
    }

    if (includes(emailLimitBackendErrors, backendError)) {
        errors[SendTestErrorNames.NO_EMAIL_CREDITS] = true;
    }

    if (backendError) {
        errors[SendTestErrorNames.OTHER_BACKEND_ERROR] = true;
    }
    if (
        backendError ===
        BackendErrors.TEMPLATE_SIZE_EXCEEDS_LIMIT_SEND_TEST.replace(INDEX_PLACEHOLDER, `${EmailSizeLimit}`)
    ) {
        errors[SendTestErrorNames.TEMPLATE_SIZE] = true;
        errors[SendTestErrorNames.OTHER_BACKEND_ERROR] = false;
    } else return { errors };
}

function* sendTest(): any {
    const { sendTest } = yield select(getTemplateEditor);
    const { toEmails, fromName, fromEmail, subject, editorData, previewText } = sendTest;

    const recipientsArray = toEmails.split(';').map((item) => item.trim());
    const Sender: Sender = {
        From: [fromEmail],
        FromName: fromName,
    };
    const Recipients: TransactionalRecipient = {
        To: toEmails?.split(';'),
    };
    const email: EmailTransactionalMessage = {
        Body: {
            Subject: subject,
            Parts: [
                {
                    ContentType: 'text/html',
                    Charset: editorData.charset,
                    Content: editorData.htmlContent,
                },
                ...(editorData.textContent ? [{ Content: editorData.textContent, ContentType: 'text/plain' }] : []),
                ...(editorData.ampContent ? [{ Content: editorData.ampContent, ContentType: 'text/x-amp-html' }] : []),
            ],
            PreHeader: previewText,
        },
        Sender,
        Recipients,
    };

    try {
        yield put(UpdateDialog({ isLoading: true }));
        const { errors } = yield call(verifySendTest, recipientsArray, null);
        yield put(SetSendTestData({ ...sendTest, errors }));
        if (!errors[SendTestErrorNames.INVALID_EMAILS] && !errors[SendTestErrorNames.INVALID_EMAILS_STATUS]) {
            yield call(sendEmail, email);
            yield put(ShowInfoToast(i18n.t('testemailsent')));
            yield put(HideDialog());
        } else {
            yield put(UpdateDialog({ isLoading: false }));
        }
    } catch (e) {
        yield put(UpdateDialog({ isLoading: false }));
        const { errors } = yield call(verifySendTest, null, e?.response?.data?.Error);
        yield put(SetSendTestData({ ...sendTest, errors }));
        if (errors[SendTestErrorNames.OTHER_BACKEND_ERROR]) {
            yield put(ShowErrorToast(e));
        }
    }
}

function* uploadEditorFile(action: UploadEditorFileAction): any {
    const { file, blockId, isLinkToFile } = action.payload;
    const { files = [] } = yield select(getEditorFilesStore);
    const { PublicAccountID } = yield select(getGeneralInfo);
    const userFiles = yield select(getUserFilesStore);
    const filesList = userFiles?.files || [];

    try {
        yield put(UploadEditorFileStart());
        let fileName = file.name;
        const invalidChars = ['\\', '/', ':', '*', '?', '"', '<', '>', '|', '+', '#', '&', '%', ' '];
        if (invalidChars.some((char) => file.name.includes(char))) {
            fileName = file.name.replace(INVALID_FILENAME_CHARACTERS_REGEX, '_');
        }

        const newFile: File = new File([file], fileName, { type: file.type });
        const uploadedFile = yield call(uploadFile, { file: newFile });
        const fileUrl = `${APIPATH}/userfile/${PublicAccountID}/${uploadedFile.FileName}`;

        const newUploadedFile: EditorFile = {
            fileName: uploadedFile.FileName,
            fileUrl,
            blockId,
            isLinkToFile,
        };

        yield put(UploadEditorFileSuccess([...files, newUploadedFile]));
        if (filesList.length) {
            yield put(FileLoadSuccess([...filesList, uploadedFile]));
        }
    } catch (e) {
        console.log(e);
        yield put(ShowErrorToast(e));
        yield put(UploadEditorFileError());
    }
}

function* onClose(): any {
    const { isOpened } = yield select(getDialogStore);
    const { isOpened: isMediaManagerOpened } = yield select(getMediaManagerStore);
    if (isOpened) {
        yield put(HideDialog());
    }
    if (isMediaManagerOpened) {
        yield put(HideMediaManager());
    }
}

function* load(action: LoadStartAction): any {
    const { payload } = action;
    const { name, isPublic, useDraftScope } = payload;

    try {
        if (!name) {
            const getTemplate = yield call(getDefaultTemplateObj, TemplateType.TemplateEditor);
            yield put(SetDefaultTemplate({ templateObject: getTemplate?.data }));
        } else {
            const template: Template = yield call(fetchTemplate, name, isPublic);
            yield put(LoadSuccess(template, isPublic, useDraftScope));
        }
    } catch (e) {
        Logger.error(e?.response?.data?.Error ?? e);
        yield put(LoadError());
        yield put(ShowErrorToast(e));
    }
}

function* save({ payload }: SaveStartAction): any {
    const {
        data: { name },
        exitOnSave,
        useIndividualPaths,
        onSaveAndExitCallback,
    } = payload;

    const { isNewTemplate, templateName, templateObject } = yield select(getTemplateEditor);

    try {
        if (isNewTemplate) {
            yield call(saveTemplate, templateObject);
            yield put(SaveSuccess());
        } else {
            yield call(updateTemplate, templateName, templateObject);
            yield put(SaveSuccess());
        }
        if (exitOnSave && onSaveAndExitCallback) {
            yield call(onSaveAndExitCallback, templateObject?.Name, templateObject);
        }

        if (!useIndividualPaths) {
            return;
        }
        if (!exitOnSave) {
            const newName = safelyEncodeBase64Url(name);
            const url = TemplatesPaths.TemplateEditorLinkName.replace(`${pathsParams.template}`, `${newName}`);
            yield put(replace(url));
        } else {
            yield put(replace(TemplatesPaths.TemplateList));
        }
    } catch (e) {
        Logger.error(e?.response?.data?.Error ?? e.message);
        if (e?.response?.data?.MessageID === 'template_name_in_use') {
            yield put(SaveError(e?.response?.data?.Error));
            return;
        }
        yield put(ShowErrorToast(e));
        yield put(SaveError());
    }
}

function* watchLoadStart(): any {
    yield takeEvery(TemplateEditorActionTypes.SEND_TEST, sendTest);
    yield takeEvery(TemplateEditorActionTypes.SAVE_START, save);
    yield takeEvery(TemplateEditorActionTypes.LOAD_START, load);
    yield takeEvery(TemplateEditorActionTypes.UPLOAD_FILE, uploadEditorFile);
    yield takeLatest(TemplateEditorActionTypes.CLOSE, onClose);
}

const templateEditorSagas = [fork(watchLoadStart)];

export default templateEditorSagas;
